import createMessageHandler from 'src/utils/createMessageHandler'

import {
  CONNECT,
  CONNECT_ACK,
  PRIVATE_PROTOCOL_VERSION,
  DEFAULT_HANDSHAKE_TIMEOUT_MS,
} from './protocol'

// MessageChannel-based transport for private (internal) cross-frame
// messaging. Host side runs on the page that loads our iframes (embed
// script on a partner page, the FLP hosting our widgets, our own
// internal pages); client side runs in the iframe.
//
// Each iframe gets one channel. All private message types from that
// iframe flow through it. Setup is an iframe-initiated handshake:
//
//   iframe → parent  : window.postMessage({ type: 'connect',
//                       meta: { supportedVersions: [...] } })
//   parent → iframe  : window.postMessage({ type: 'connectAck',
//                       meta: { version: <agreed> } }, [port2])
//   from then on     : both sides communicate over the transferred port
//
// Public (partner-facing, broadcast) messages do not use this channel —
// they continue to go out via window.postMessage to the parent's
// window-level message listeners. See send.js (TODO) for the public
// path.

// Pick the highest version present in both lists; return null if none.
const negotiateVersion = (ours, theirs) => {
  const theirSet = new Set(theirs)
  return ours
    .slice()
    .sort((a, b) => b - a)
    .find(v => theirSet.has(v)) ?? null
}

// Host side. Owns one MessageChannel per registered iframe.
//
// Usage:
//   const host = createChannelHost({ origin: 'https://pledge.to' })
//   host.register(iframe)
//   host.on(iframe, 'widgetHeightChanged', ({ height }) => ...)
//   host.send(iframe, 'modalClose')          // queued if not yet connected
//   host.unregister(iframe)                  // when iframe is destroyed
export const createChannelHost = ({
  origin,
  supportedVersions = [ PRIVATE_PROTOCOL_VERSION ],
} = {}) => {
  // iframe element → { port, version, handlers (Map), pendingOutbound }
  const records = new Map()
  // type → Set<handler> for "fire for any iframe" subscriptions
  const globalHandlers = new Map()

  const findIframe = source => {
    for (const iframe of records.keys())
      if (iframe.contentWindow === source) return iframe
    return null
  }

  const dispatchInbound = (iframe, envelope) => {
    const record = records.get(iframe)
    if (!record) return
    record.handlers.get(envelope?.type)?.(envelope.payload, envelope.meta ?? {})
    globalHandlers.get(envelope?.type)?.forEach(handler =>
      handler(envelope.payload, envelope.meta ?? {}, iframe))
  }

  const sendOnPort = (record, type, payload, meta) => {
    record.port.postMessage({
      type,
      payload,
      meta: { version: record.version, ...meta },
    })
  }

  const handleConnect = event => {
    if (event.data?.type !== CONNECT) return

    const iframe = findIframe(event.source)
    if (!iframe) return

    const record = records.get(iframe)

    const agreed = negotiateVersion(
      supportedVersions,
      event.data?.meta?.supportedVersions ?? [],
    )
    if (agreed == null) return // no compatible version

    // If we already have a port, the iframe must be reconnecting —
    // typically because the iframe navigated to a new same-origin
    // document, which preserves the WindowProxy (so findIframe still
    // matches) but discards the previous JS context (so the old
    // port's other end is gone). Close the stale port and stand up
    // a fresh channel. Handler registrations on `record.handlers`
    // carry over since they're attached to the iframe, not the port.
    if (record.port) record.port.close()

    const channel = new MessageChannel()
    record.port = channel.port1
    record.version = agreed
    record.port.onmessage = e => dispatchInbound(iframe, e.data)

    // Flush queued outbound.
    record.pendingOutbound.forEach(({ type, payload, meta }) =>
      sendOnPort(record, type, payload, meta))
    record.pendingOutbound.length = 0

    event.source.postMessage(
      { type: CONNECT_ACK, meta: { version: agreed } },
      event.origin,
      [ channel.port2 ],
    )
  }

  addEventListener('message', createMessageHandler(origin, handleConnect))

  return {
    register(iframe) {
      if (records.has(iframe)) return
      records.set(iframe, {
        port: null,
        version: null,
        handlers: new Map(),
        pendingOutbound: [],
      })
    },

    unregister(iframe) {
      const record = records.get(iframe)
      if (record?.port) record.port.close()
      records.delete(iframe)
    },

    on(iframe, type, handler) {
      const record = records.get(iframe)
      if (!record) throw new Error('iframe not registered with channel host')
      // Per-iframe addressing is single-handler by design — the second
      // .set() would silently drop the first. Warn so a re-registration
      // without an intervening unregister surfaces at mount time
      // instead of as a quietly-missing event later. Use onAll() if
      // multiple subscribers per type are actually wanted.
      if (record.handlers.has(type))
        console.warn(
          `channel host.on: replacing existing '${type}' handler for `
          + 'an iframe. Use onAll if multiple handlers per type are needed.'
        )
      record.handlers.set(type, handler)
    },

    // Fire `handler(payload, meta, iframe)` for every message of the
    // given type from any registered iframe. Useful for receivers that
    // don't care which iframe sent it (e.g. rainHearts firing on any
    // donate widget, FLP picking up showModal / commentAdded
    // from any embedded donate iframe). Multiple handlers per type are
    // supported and all fire — e.g. FLP renders one Donation component
    // per order and each subscribes to filter by order_id. Returns a
    // teardown to unsubscribe.
    onAll(type, handler) {
      if (!globalHandlers.has(type)) globalHandlers.set(type, new Set())
      globalHandlers.get(type).add(handler)
      return () => globalHandlers.get(type)?.delete(handler)
    },

    send(iframe, type, payload, meta = {}) {
      const record = records.get(iframe)
      if (!record) throw new Error('iframe not registered with channel host')
      if (!record.port) {
        record.pendingOutbound.push({ type, payload, meta })
        return
      }
      sendOnPort(record, type, payload, meta)
    },

    isConnected(iframe) {
      return !!records.get(iframe)?.port
    },
  }
}

// Client side. Runs inside the iframe. Initiates handshake at
// construction, exposes `.ready` for callers that need to wait, and
// buffers outbound messages until the channel is up.
//
// Usage:
//   const client = createChannelClient()
//   client.on('modalClose', () => ...)
//   client.send('widgetHeightChanged', { height: 480 })  // queued if not yet ready
//   await client.ready                                    // optional
export const createChannelClient = ({
  supportedVersions = [ PRIVATE_PROTOCOL_VERSION ],
  timeoutMs = DEFAULT_HANDSHAKE_TIMEOUT_MS,
} = {}) => {
  let port = null
  let version = null
  const handlers = new Map()
  const allHandlers = new Map()
  const pendingOutbound = []

  const dispatchInbound = envelope => {
    handlers.get(envelope?.type)?.(envelope.payload, envelope.meta ?? {})
    allHandlers.get(envelope?.type)?.forEach(handler =>
      handler(envelope.payload, envelope.meta ?? {}))
  }

  const sendOnPort = (type, payload, meta) => {
    port.postMessage({
      type,
      payload,
      meta: { version, ...meta },
    })
  }

  const ready = new Promise((resolve, reject) => {
    if (parent === self) {
      reject(new Error('No parent frame; channel client cannot connect.'))
      return
    }

    const handleAck = e => {
      if (e.source !== parent) return
      if (e.data?.type !== CONNECT_ACK) return
      if (!e.ports?.[0]) return

      port = e.ports[0]
      version = e.data?.meta?.version
      port.onmessage = ev => dispatchInbound(ev.data)

      pendingOutbound.forEach(({ type, payload, meta }) =>
        sendOnPort(type, payload, meta))
      pendingOutbound.length = 0

      removeEventListener('message', handleAck)
      clearTimeout(timeoutId)
      resolve({ version })
    }

    addEventListener('message', handleAck)

    parent.postMessage(
      { type: CONNECT, meta: { supportedVersions } },
      '*',
    )

    const timeoutId = setTimeout(() => {
      removeEventListener('message', handleAck)
      reject(new Error('Channel handshake timed out.'))
    }, timeoutMs)
  })

  // Swallow unhandled rejection — callers opt in via `.ready` if they
  // want to know.
  ready.catch(() => {})

  return {
    ready,

    on(type, handler) {
      // Single-handler by design, same as the host-side on(). Warn
      // when a re-registration silently replaces the previous handler
      // so an accidental double-registration surfaces at mount time
      // instead of as a quietly-missing event later. The client has
      // a single iframe context so this case is rarer than the host
      // side, but the warning is essentially free and keeps the two
      // surfaces symmetric.
      if (handlers.has(type))
        console.warn(
          `channel client.on: replacing existing '${type}' handler.`
        )
      handlers.set(type, handler)
    },

    // Multi-handler counterpart of on(); same shape as host.onAll.
    // Multiple subscribers per type all fire — useful for callers
    // that mount + unmount across the page's lifetime (React effects
    // with cleanup) where the single-handler replace-and-warn shape
    // of on() is too restrictive. Returns a teardown that
    // unsubscribes; matches host.onAll's contract.
    onAll(type, handler) {
      if (!allHandlers.has(type)) allHandlers.set(type, new Set())
      allHandlers.get(type).add(handler)
      return () => allHandlers.get(type)?.delete(handler)
    },

    send(type, payload, meta = {}) {
      if (!port) {
        pendingOutbound.push({ type, payload, meta })
        return
      }
      sendOnPort(type, payload, meta)
    },

    isConnected() {
      return !!port
    },
  }
}
